import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updateEmailVerificationStatus } from '../lib/userService';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../lib/firebase';

export const EmailConfirmed: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Get Firebase action parameters
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const success = searchParams.get('success');

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Handle Firebase email verification
        if (mode === 'verifyEmail' && oobCode) {
          // First, check what email this action code is for
          const actionCodeInfo = await checkActionCode(auth, oobCode);
          const actionEmail = actionCodeInfo.data.email;
          const currentUser = auth.currentUser;

          // Check if wrong user is logged in
          if (currentUser && currentUser.email !== actionEmail) {
            setVerificationStatus('error');
            setErrorMessage(`This verification link is for ${actionEmail}, but you're currently signed in as ${currentUser.email}. Please sign out and sign in with the correct account.`);
            return;
          }

          // Apply the verification code
          await applyActionCode(auth, oobCode);
          setVerificationStatus('success');

          // Update Firestore using auth.currentUser directly (more reliable than useAuth hook)
          if (currentUser) {
            await updateEmailVerificationStatus(currentUser.uid, true);
          }
        } else if (success === 'true') {
          // Fallback for direct success parameter
          setVerificationStatus('success');
          const currentUser = auth.currentUser;
          if (currentUser) {
            await updateEmailVerificationStatus(currentUser.uid, true);
          }
        } else {
          // No params means Firebase verified server-side with handleCodeInApp:false
          // Check if user is signed in and recently verified
          const currentUser = auth.currentUser;
          if (currentUser) {
            // Reload user to get fresh emailVerified status
            await currentUser.reload();
            if (currentUser.emailVerified) {
              setVerificationStatus('success');
              await updateEmailVerificationStatus(currentUser.uid, true);
            } else {
              setVerificationStatus('error');
              setErrorMessage('Email verification is pending. Please check your email and click the verification link.');
            }
          } else {
            setVerificationStatus('error');
            setErrorMessage('Invalid verification link or link has expired.');
          }
        }
      } catch (error: any) {
        console.error('Email verification error:', error);
        setVerificationStatus('error');
        if (error.code === 'auth/invalid-action-code') {
          setErrorMessage('This verification link is invalid or has already been used.');
        } else if (error.code === 'auth/expired-action-code') {
          setErrorMessage('This verification link has expired. Please request a new verification email.');
        } else {
          setErrorMessage('An error occurred while verifying your email. Please try again.');
        }
      }
    };

    handleEmailVerification();
  }, [mode, oobCode, success]);

  // Separate useEffect for navigation to avoid dependency issues
  useEffect(() => {
    if (verificationStatus === 'success') {
      // Check if user is signed in before redirecting to app
      const currentUser = auth.currentUser;

      if (currentUser) {
        // User is signed in, redirect to app with setup modal
        const timer = setTimeout(() => {
          navigate('/app?setup=true');
        }, 3000); // Wait 3 seconds before redirect

        return () => clearTimeout(timer);
      }
      // If user is not signed in, don't auto-redirect
      // They can manually click "Go to Dashboard" which will prompt them to sign in
    }
  }, [verificationStatus, navigate]);

  // Loading state
  if (verificationStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verifying Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (verificationStatus === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Email Verification Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {errorMessage}
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is signed in for conditional messaging
  const isSignedIn = auth.currentUser !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Email Confirmed!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your email address has been successfully verified. Welcome to Wonderstood!
          </p>
          {isSignedIn ? (
            <p className="mt-4 text-sm text-gray-500">
              You'll be redirected to your dashboard in 3 seconds...
            </p>
          ) : (
            <p className="mt-4 text-sm text-gray-500">
              Please sign in to continue setting up your profile.
            </p>
          )}

          <div className="mt-6 space-y-3">
            <button
              onClick={() => navigate(isSignedIn ? '/app?setup=true' : '/login?verified=true')}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isSignedIn ? 'Go to Dashboard' : 'Sign In'}
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Return to Home
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                What's Next?
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Complete your profile setup</li>
                  <li>Browse available professionals</li>
                  <li>Book your first consultation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmed;